import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import {
  TabContainer,
  Tab,
  PageHeader,
  DecorativeLink,
  SimpleDataTable,
  Section,
  DangerousWrapper,
  slugify
} from '@budget-monorepo/shared-ui';
import Layout from '../components/Layout';
import HelmetData from '../components/HelmetData';

const LocalAidPage = (props) => {
  const { location, data: { download, site, content } } = props;
  // eslint-disable-next-line camelcase
  const { modifiedTime, mg_mod_date, mg_mod_datetime } = content.parent;
  const layoutProps = {
    activeIndex: 3,
    above: () => <PageHeader title={content.title} category={content.budgetSection} />,
    pageTitle: content.title,
    dataTimeStamp: modifiedTime,
    buildTimeStamp: site.buildTime
  };

  const urlToTabIndex = (loc) => {
    if (!loc.href) {
      return null;
    }
    const url = new URL(loc.href);
    const params = new URLSearchParams(url.search);
    const param = params.get('tab');
    let index = 0;
    content.tabs.every((tab, tabIndex) => {
      if (slugify(tab.title) === param) {
        index = tabIndex;
        return false;
      }
      return true;
    });
    return index;
  };

  const onTabChange = (tabRef, curLocation) => {
    if (global.window && curLocation.href) {
      const tab = tabRef.current.innerText;
      const { pathname } = location;
      const url = `${pathname}?tab=${slugify(tab)}`;
      // eslint-disable-next-line no-undef
      window.history.replaceState({}, '', url);
    }
  };
  // console.log(urlToTabIndex(location))

  const metaTitle = content.title;
  const metaDescription = 'Local aid distribution from the state to regional school districts and municipalities.';
  return(
    <Fragment>
      <HelmetData {...{
        metaTitle,
        metaDescription,
        metaType: 'dataset',
        mg_mod_date,
        mg_mod_datetime
      }}
      />
      <Layout {...layoutProps}>
        {download && (
          <Section>
            <DecorativeLink
              /* Create a reference link to the local aid data file */
              href={download.publicURL}
              text={`Download the ${content.title} Data`}
              showFileIcon
            />
          </Section>
        )}
        <DangerousWrapper rawHtml={content.summary} />
        <Section>
          <SimpleDataTable title={`${content.title} ${content.budgetSection} totals`} table={content.totalTable} maxColWidth={125} download={`${location.pathname}-totals`} />
        </Section>
        <Section>
          {
            global.window && (
              <TabContainer defaultTab={urlToTabIndex(location, 'tab-rates')} onTabChange={(tabRef) => onTabChange(tabRef, location, 'tab-rates')}>
                {
                  content.tabsRate.map((tab, index) => {
                    const tabKey = `local-rates-tab.${index}`;
                    return(
                      <Tab key={tabKey} title={tab.title}>
                        <SimpleDataTable title={`${content.title} ${tab.title}`} table={content[tab.value]} maxColWidth={150} download={`${location.pathname}-${tab.title}`} />
                      </Tab>
                    );
                  })
                }
              </TabContainer>
            )
          }
        </Section>
        <Section>
          {
            global.window && (
              <TabContainer defaultTab={urlToTabIndex(location)} onTabChange={(tabRef) => onTabChange(tabRef, location)}>
                {
                  content.tabs.map((tab, index) => {
                    const tabKey = `local-aid-tab.${index}`;
                    return(
                      <Tab key={tabKey} title={tab.title}>
                        <SimpleDataTable title={`${content.title} ${content.budgetSection} ${tab.title}`} table={content[tab.value]} maxColWidth={125} download={`${props.location.pathname}-${tab.title}`} />
                      </Tab>
                    );
                  })
                }
              </TabContainer>
            )
          }
        </Section>
      </Layout>
    </Fragment>
  );
};

LocalAidPage.propTypes = {
  data: PropTypes.object,
  location: PropTypes.object
};

export default LocalAidPage;

export const query = graphql`
  query localAidJson {
    site {
      ...SiteMetadata
    }
    # Fetch the url of the excel file containing the local aid dataset for an in page download link.
    # If the file name changes, the query here (eq: "sect3.xls") will need to be updated.
    download: file(base: { eq: "sect3.xls" }) {
      publicURL
    }
    # Fetch all of the data from the local-aid.json file node.
    content: pagesJson(fields: { slug: { eq: "/local-aid/" } }) {
      title
      budgetSection
      summary
      tabs {
        title
        value
      }
      tabsRate {
        title
        value
      }
      baseRateTable {
        filterable
        headers {
          name
          type
          align
          value
        }
        data {
          baseSchool
          baseAdmin
          baseInstruct
          baseTeach
          baseServ
          baseProf
          baseEquip
          baseGuide
          basePupil
          baseOp
          baseEmp
          baseSpecial
          baseTot
        }
      }
      incRateTable {
        filterable
        headers {
          name
          type
          align
          value
        }
        data {
          incSchool
          incAdmin
          incInstruct
          incTeach
          incServ
          incProf
          incEquip
          incGuide
          incPupil
          incOp
          incEmp
          incSpecial
          incTot
        }
      }
      municipalityTable {
        filterable
        headers {
          name
          type
          align
          value
        }
        total {
          municipality
          chapter70
          unrestrictedGeneralGovernmentAid
        }
        data {
          municipality
          chapter70
          unrestrictedGeneralGovernmentAid
        }
      }
      schoolTable {
        filterable
        headers {
          name
          type
          align
          value
        }
        total {
          regionalSchoolDistrict
          chapter70
        }
        data {
          regionalSchoolDistrict
          chapter70
        }
      }
      totalTable {
        headers {
          name
          type
          align
          value
        }
        total {
          total
          chapter70
          unrestrictedGeneralGovernmentAid
        }
        data {
          total
          chapter70
          unrestrictedGeneralGovernmentAid
        }
      }
      # Get the last modified timestamp for the file powering the page
      ...PagesJsonDataTimeStamp
    }
  }
`;
